import {useContext, useMemo} from "react";
import _cloneDeep from "lodash/cloneDeep";
import _get from "lodash/get";
import _set from "lodash/set";
import _startCase from "lodash/startCase";

import FormContext from "../form-context";
import CheckboxList from "./checkbox-list";

const EXCLUDED_FIELD_IDS = new Set([
    "_id",
    "_links",
    "additionalCategories",
    "category",
    "categoryUuid",
    "categoryUuids",
    "costExTax",
    "createdAt",
    "description",
    "discountedSellingPrice",
    "id",
    "images",
    "imperfectionsGallery",
    "options",
    "primaryCategoryUuid",
    "pricingMethod",
    "productTemplate",
    "productTemplateUuid",
    "seedProfile",
    "sellingPrice",
    "thumbnail",
    "title",
    "updatedAt",
    "uri",
    "userDefinedFieldsets",
    "video"
]);

const EXCLUDED_FIELD_TYPES = new Set([
    "action-input",
    "file",
    "image-uploader",
    "rich-text"
]);
const MAX_SELECTED_PRODUCT_TYPES = 5;

const toUniqueList = (values = []) => {
    const seen = new Set();
    return values.filter((value) => {
        const normalized = String(value || "").trim();
        if (!normalized || seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
    });
};

const parseMatchFields = (value, csvValue) => {
    const list = [];

    if (Array.isArray(value)) {
        list.push(...value);
    } else if (typeof value === "string") {
        list.push(...value.split(","));
    }

    if (typeof csvValue === "string") {
        list.push(...csvValue.split(","));
    }

    return toUniqueList(list.map((item) => String(item || "").trim()));
};

const getParentPath = (name = "") => name.split(".").slice(0, -1).join(".");

const isPrimitiveMatchValue = (value) => {
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value === "boolean") return true;
    return false;
};

const isMatchableValue = (value) => {
    if (Array.isArray(value)) {
        return value.some(isPrimitiveMatchValue);
    }

    return isPrimitiveMatchValue(value);
};

const getFieldLabel = (fieldId, label) => {
    const normalizedId = String(fieldId || "").trim();
    if (!normalizedId) return "";
    return String(label || "").trim() || _startCase(normalizedId);
};

const getPreviewProduct = (values, contextValues) => {
    return _get(values, "values.referenceProduct")
        || _get(contextValues, "values.referenceProduct")
        || _get(contextValues, "referenceProduct")
        || null;
};

const getProductTemplate = (values, contextValues) => {
    return _get(values, "values.productDetailProductTemplate")
        || _get(contextValues, "values.productDetailProductTemplate")
        || _get(contextValues, "productDetailProductTemplate")
        || null;
};

const getProductTemplates = (values, contextValues) => {
    return _get(values, "values.productDetailProductTemplates")
        || _get(contextValues, "values.productDetailProductTemplates")
        || _get(contextValues, "productDetailProductTemplates")
        || [];
};

const getProductTemplateUuid = (template) => {
    return String(template?.uuid || template?.id || template?._id || "").trim();
};

const getProductTemplateLabel = (template) => {
    return String(template?.title || template?.name || template?.label || "Product Type").trim();
};

const getCheckboxListId = (path) => {
    return String(path || "product-similarity-fields").replace(/[^a-zA-Z0-9_-]+/g, "-");
};

const SubfieldHeader = ({label, description}) => {
    return (
        <div>
            <div className="block text-sm font-semibold text-gray-700">{label}</div>
            {description ? (
                <p className="mt-1 mb-0 text-xs font-medium text-gray-500 whitespace-normal">
                    {description}
                </p>
            ) : null}
        </div>
    );
};

const getTemplateFieldOptions = (productTemplate) => {
    if (!productTemplate) return [];

    const options = [];
    const seen = new Set();

    const addOption = (fieldId, label, type) => {
        const normalizedId = String(fieldId || "").trim();
        if (!normalizedId || seen.has(normalizedId) || EXCLUDED_FIELD_IDS.has(normalizedId)) return;
        if (EXCLUDED_FIELD_TYPES.has(type)) return;

        options.push({
            label: getFieldLabel(normalizedId, label),
            value: normalizedId
        });
        seen.add(normalizedId);
    };

    (productTemplate.userDefinedFieldsets || []).forEach((fieldset) => {
        (fieldset?.fields || []).forEach((field) => {
            addOption(field?.id, field?.label, field?.type);
        });
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
};

const getSharedTemplateFieldOptions = (templates = []) => {
    const templateOptions = (templates || [])
        .filter(Boolean)
        .map(template => getTemplateFieldOptions(template));

    if (!templateOptions.length) return [];

    const optionMaps = templateOptions.map(options => new Map(options.map(option => [option.value, option])));
    const firstOptions = templateOptions[0] || [];

    return firstOptions
        .filter(option => optionMaps.every(map => map.has(option.value)))
        .sort((a, b) => a.label.localeCompare(b.label));
};

const getPreviewProductFieldOptions = (referenceProduct) => {
    if (!referenceProduct) return [];

    const options = [];
    const seen = new Set();

    const addOption = (fieldId, label) => {
        const normalizedId = String(fieldId || "").trim();
        if (!normalizedId || seen.has(normalizedId) || EXCLUDED_FIELD_IDS.has(normalizedId)) return;

        const value = _get(referenceProduct, normalizedId);
        if (!isMatchableValue(value)) return;

        options.push({
            label: getFieldLabel(normalizedId, label),
            value: normalizedId
        });
        seen.add(normalizedId);
    };

    (referenceProduct.userDefinedFieldsets || []).forEach((fieldset) => {
        (fieldset?.fields || []).forEach((field) => {
            addOption(field?.id, field?.label);
        });
    });

    Object.entries(referenceProduct || {}).forEach(([fieldId]) => {
        addOption(fieldId);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
};

export default function ProductSimilarityFields(props) {
    const context = useContext(FormContext);
    const parentPath = useMemo(() => getParentPath(props.name), [props.name]);
    const csvPath = parentPath ? `${parentPath}.matchFieldsCsv` : "matchFieldsCsv";
    const matchAcrossProductTypesPath = parentPath ? `${parentPath}.matchAcrossProductTypes` : "matchAcrossProductTypes";
    const candidateTemplatePath = parentPath ? `${parentPath}.candidateTemplateUuids` : "candidateTemplateUuids";
    const candidateTemplateCsvPath = parentPath ? `${parentPath}.candidateTemplateUuidsCsv` : "candidateTemplateUuidsCsv";
    const csvValue = _get(context.values, csvPath);
    const matchAcrossProductTypes = _get(context.values, matchAcrossProductTypesPath) === true;
    const candidateTemplateValue = _get(context.values, candidateTemplatePath);
    const candidateTemplateCsvValue = _get(context.values, candidateTemplateCsvPath);
    const productTemplate = useMemo(() => getProductTemplate(props.values, context.values), [context.values, props.values]);
    const productTemplates = useMemo(() => getProductTemplates(props.values, context.values), [context.values, props.values]);
    const referenceProduct = useMemo(() => getPreviewProduct(props.values, context.values), [context.values, props.values]);
    const currentTemplateUuid = useMemo(() => getProductTemplateUuid(productTemplate), [productTemplate]);
    const productTemplatesByUuid = useMemo(() => {
        const map = new Map();

        [productTemplate, ...(productTemplates || [])].filter(Boolean).forEach((template) => {
            const uuid = getProductTemplateUuid(template);
            if (uuid && !map.has(uuid)) {
                map.set(uuid, template);
            }
        });

        return map;
    }, [productTemplate, productTemplates]);
    const selectedCandidateTemplateUuids = useMemo(() => {
        return parseMatchFields(candidateTemplateValue, candidateTemplateCsvValue)
            .filter(uuid => uuid !== currentTemplateUuid)
            .filter(uuid => productTemplatesByUuid.has(uuid));
    }, [candidateTemplateCsvValue, candidateTemplateValue, currentTemplateUuid, productTemplatesByUuid]);
    const activeCandidateTemplateUuids = matchAcrossProductTypes ? selectedCandidateTemplateUuids : [];
    const selectedTemplateUuids = useMemo(() => {
        return toUniqueList([currentTemplateUuid, ...activeCandidateTemplateUuids].filter(Boolean));
    }, [activeCandidateTemplateUuids, currentTemplateUuid]);
    const selectedTemplates = useMemo(() => {
        return selectedTemplateUuids
            .map(uuid => productTemplatesByUuid.get(uuid))
            .filter(Boolean);
    }, [productTemplatesByUuid, selectedTemplateUuids]);
    const candidateTemplateOptions = useMemo(() => {
        const options = [...productTemplatesByUuid.values()]
            .map((template) => {
                const uuid = getProductTemplateUuid(template);
                if (!uuid || uuid === currentTemplateUuid) return null;

                return {
                    label: getProductTemplateLabel(template),
                    value: uuid,
                    disabled: !selectedCandidateTemplateUuids.includes(uuid)
                        && selectedCandidateTemplateUuids.length >= MAX_SELECTED_PRODUCT_TYPES - 1
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.label.localeCompare(b.label));

        return options;
    }, [currentTemplateUuid, productTemplatesByUuid, selectedCandidateTemplateUuids]);
    const fieldOptions = useMemo(() => {
        const templateOptions = selectedTemplates.length
            ? getSharedTemplateFieldOptions(selectedTemplates)
            : getTemplateFieldOptions(productTemplate);
        return templateOptions.length ? templateOptions : getPreviewProductFieldOptions(referenceProduct);
    }, [productTemplate, referenceProduct, selectedTemplates]);
    const selectedValues = useMemo(() => parseMatchFields(props.value, csvValue), [csvValue, props.value]);
    const optionValues = useMemo(() => new Set(fieldOptions.map((field) => field.value)), [fieldOptions]);
    const selectedOptionValues = selectedValues.filter((value) => optionValues.has(value));
    const customValues = selectedValues.filter((value) => !optionValues.has(value));
    const hasTemplateChoices = currentTemplateUuid && candidateTemplateOptions.length > 0;
    const templateFieldLabel = props.templateFieldLabel || "Product Types To Include";
    const templateDescription = props.templateDescription || "Choose the product types that can appear here. This product type is always included.";

    const updateFields = (nextPresetValues = []) => {
        const nextValues = toUniqueList([...(nextPresetValues || []), ...customValues]);
        const nextComponent = _cloneDeep(props.values || {});

        _set(nextComponent, props.name, nextValues);
        _set(nextComponent, csvPath, nextValues.join(","));

        props.onComponentChange?.({target: {name: props.name}}, nextComponent);
    };

    const updateCandidateTemplates = (nextTemplateValues = []) => {
        const nextCandidateValues = toUniqueList((nextTemplateValues || [])
            .filter(uuid => uuid !== currentTemplateUuid)
            .filter(uuid => productTemplatesByUuid.has(uuid)))
            .slice(0, MAX_SELECTED_PRODUCT_TYPES - 1);
        const nextSelectedTemplates = toUniqueList([currentTemplateUuid, ...nextCandidateValues].filter(Boolean))
            .map(uuid => productTemplatesByUuid.get(uuid))
            .filter(Boolean);
        const nextFieldOptions = getSharedTemplateFieldOptions(nextSelectedTemplates);
        const nextOptionValues = new Set(nextFieldOptions.map(field => field.value));
        const nextSelectedValues = selectedValues.filter(value => nextOptionValues.has(value));
        const nextComponent = _cloneDeep(props.values || {});

        _set(nextComponent, candidateTemplatePath, nextCandidateValues);
        _set(nextComponent, candidateTemplateCsvPath, nextCandidateValues.join(","));
        _set(nextComponent, props.name, nextSelectedValues);
        _set(nextComponent, csvPath, nextSelectedValues.join(","));

        props.onComponentChange?.({target: {name: candidateTemplatePath}}, nextComponent);
    };

    if (!fieldOptions.length && !(hasTemplateChoices && matchAcrossProductTypes)) return null;

    return (
        <div className="space-y-5">
            {hasTemplateChoices && matchAcrossProductTypes ? (
                <div className="space-y-2">
                    <SubfieldHeader
                        label={templateFieldLabel}
                        description={templateDescription}
                    />
                    <CheckboxList
                        className="!grid-cols-1"
                        disabled={props.disabled}
                        id={getCheckboxListId(candidateTemplatePath)}
                        modified={props.modified}
                        name={candidateTemplatePath}
                        onChange={(event, fieldName, nextTemplateValues) => {
                            updateCandidateTemplates(nextTemplateValues);
                        }}
                        options={candidateTemplateOptions}
                        value={selectedCandidateTemplateUuids}
                    />
                </div>
            ) : null}

            {fieldOptions.length ? (
                <div className="space-y-2">
                    {props.fieldLabel ? (
                        <SubfieldHeader
                            label={props.fieldLabel}
                            description={props.fieldDescription}
                        />
                    ) : null}
                    <CheckboxList
                        className="!grid-cols-1 md:!grid-cols-2"
                        disabled={props.disabled}
                        id={getCheckboxListId(props.name)}
                        modified={props.modified}
                        name={props.name}
                        onChange={(event, fieldName, nextPresetValues) => {
                            updateFields(nextPresetValues);
                        }}
                        options={fieldOptions}
                        value={selectedOptionValues}
                    />
                </div>
            ) : null}
        </div>
    );
}
